import type {
  CoachRuntimeServiceReadModel,
  CoachServiceAssignmentOperatorResult,
  OperatorIdentity,
  TableSnapshot,
} from "@billiard/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Clock3, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiRequestError,
  getCoachRuntime,
  getConnectivity,
  listCoachServiceRules,
  listCoachServiceStopRequests,
  previewCoachServiceAssignment,
  recoverLegacyCoachServiceAssignment,
  transitionCoachService,
} from "./api";
import { CoachServiceAssignmentPanel } from "./CoachServiceAssignmentPanel";
import { CoachServiceCorrectionPanel } from "./CoachServiceCorrectionPanel";
import { CoachServiceReplacementPanel } from "./CoachServiceReplacementPanel";
import { CoachServiceStopRequestPanel } from "./CoachServiceStopRequestPanel";
import {
  type CoachOperationRecovery,
  type CoachRuntimeOperationCommand,
  claimCoachOperationRecovery,
  clearCoachOperationRecovery,
  coachOperationIdentityKey,
  coachOperationOutcomeIsUnknown,
  markCoachOperationOutcomeUnknown,
  readCoachOperationRecovery,
} from "./coachOperationRecovery";
import {
  coachServiceAssignmentContext,
  readCoachServiceAssignmentRecovery,
} from "./coachServiceAssignmentRecovery";
import {
  coachServiceCorrectionContext,
  readCoachServiceCorrectionRecovery,
} from "./coachServiceCorrectionRecovery";
import {
  coachServiceReplacementContext,
  readCoachServiceReplacementRecovery,
} from "./coachServiceReplacementRecovery";
import { buildCoachSessionAssignmentAvailabilityView } from "./coachSessionAssignmentAvailability";
import { connectivityTrustState, requireTrustedStoreAuthority } from "./connectivity-trust";

type OperatorRole = OperatorIdentity["role"];
type TransitionAction = "START" | "COMPLETE";
type InUseTable = TableSnapshot & { sessionId: string };
type ConfirmedAssignmentReceipt = {
  actorId: string;
  sessionId: string;
  serviceId: string;
  tableNumber: string;
};

const serviceStatusLabels: Record<CoachRuntimeServiceReadModel["status"], string> = {
  ASSIGNED: "待上钟",
  ACTIVE: "服务中",
  COMPLETED: "已下钟",
  CANCELLED: "已取消",
  EARLY_ENDED: "提前结束",
  ABNORMAL_TERMINATED: "异常终止",
  REVERSED: "已冲正",
};

const transitionLabels: Record<TransitionAction, string> = {
  START: "确认上钟",
  COMPLETE: "正常下钟",
};

function selectedInUseTable(
  operator: Pick<OperatorIdentity, "tenantId" | "storeId">,
  table: TableSnapshot | null | undefined,
): InUseTable | null {
  return table?.state === "IN_USE" &&
    typeof table.sessionId === "string" &&
    table.sessionId.length > 0 &&
    table.tenantId === operator.tenantId &&
    table.storeId === operator.storeId
    ? (table as InUseTable)
    : null;
}

function actionsFor(
  service: CoachRuntimeServiceReadModel | undefined,
  operatorRole: OperatorRole,
): readonly TransitionAction[] {
  if (service?.status === "ASSIGNED" && operatorRole === "CASHIER") return ["START"];
  if (service?.status === "ACTIVE" && (operatorRole === "CASHIER" || operatorRole === "MANAGER")) {
    return ["COMPLETE"];
  }
  return [];
}

function operationLabel(action: TransitionAction): string {
  return transitionLabels[action];
}

function nextTransitionKey(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `coach-session-transition:${random}`;
}

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : "当前操作没有完成，请刷新后重试";
}

function fee(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function serviceStartTime(service: CoachRuntimeServiceReadModel): string {
  return service.startedAt
    ? new Date(service.startedAt).toLocaleString("zh-CN", { hour12: false })
    : "等待上钟，尚未开始计费";
}

function servicePricingRule(service: CoachRuntimeServiceReadModel): string {
  if (service.billingMode === "PER_SESSION") {
    return `按次计费：每次 ${fee(service.priceFen)}`;
  }
  const minimum =
    service.minimumMinutes > 0 ? `最低计费 ${service.minimumMinutes} 分钟` : "不设最低计费时长";
  return `按时计费：${fee(service.priceFen)}/小时；${minimum}；每 ${service.billingStepMinutes} 分钟为一个计费单位`;
}

export function CoachSessionOperationsPanel({
  operator,
  effectiveRoles,
  table: selectedTable,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  table: TableSnapshot | null | undefined;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const isCashier = operator.role === "CASHIER" && effectiveRoles.includes("CASHIER");
  const isManager = operator.role === "MANAGER" && effectiveRoles.includes("MANAGER");
  const canOperate = isCashier || isManager;
  const canView = operator.role === "OWNER" || canOperate;
  const table = selectedInUseTable(operator, selectedTable);
  const sessionId = table?.sessionId ?? null;
  const tableNumber = table?.tableNumber ?? null;
  const identityKey = useMemo(() => coachOperationIdentityKey(operator), [operator]);
  const assignmentContext = useMemo(() => coachServiceAssignmentContext(operator), [operator]);
  const replacementContext = useMemo(() => coachServiceReplacementContext(operator), [operator]);
  const correctionContext = useMemo(() => coachServiceCorrectionContext(operator), [operator]);
  const [, setRecoveryRevision] = useState(0);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [action, setAction] = useState<TransitionAction>("START");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [stopRequestRecoveryBlocked, setStopRequestRecoveryBlocked] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "INFO" | "ERROR"; text: string } | null>(null);
  const confirmedAssignmentReceiptKey = useMemo(
    () =>
      [
        "coach-confirmed-assignment-receipt",
        operator.tenantId,
        operator.storeId,
        operator.id,
        sessionId,
        tableNumber,
      ] as const,
    [operator.tenantId, operator.storeId, operator.id, sessionId, tableNumber],
  );
  const [confirmedAssignment, setConfirmedAssignment] = useState<ConfirmedAssignmentReceipt | null>(
    () =>
      queryClient.getQueryData<ConfirmedAssignmentReceipt>(confirmedAssignmentReceiptKey) ?? null,
  );
  const [connectivityClock, setConnectivityClock] = useState(() => Date.now());
  const commandFlight = useRef(false);
  const legacyAssignmentProbe = useRef<string | null>(null);

  const runtime = useQuery({
    queryKey: [
      "coach-runtime",
      operator.tenantId,
      operator.storeId,
      operator.id,
      "session",
      sessionId,
    ],
    queryFn: ({ signal }) => getCoachRuntime(undefined, undefined, sessionId ?? undefined, signal),
    enabled: canView && sessionId !== null,
    retry: false,
    refetchInterval: 1_500,
  });
  const rules = useQuery({
    queryKey: ["coach-service-rules", operator.tenantId, operator.storeId],
    queryFn: listCoachServiceRules,
    enabled: canView && sessionId !== null,
    retry: false,
    refetchInterval: 10_000,
  });
  const stopRequestDirectory = useQuery({
    queryKey: ["coach-service-stop-requests", operator.tenantId, operator.storeId, operator.id],
    queryFn: listCoachServiceStopRequests,
    enabled: canView && sessionId !== null,
    retry: false,
    refetchInterval: 2_000,
  });
  const connectivity = useQuery({
    queryKey: ["connectivity"],
    queryFn: getConnectivity,
    enabled: canView && sessionId !== null,
    retry: false,
    refetchInterval: 1_000,
  });

  useEffect(() => {
    if (!canView || sessionId === null) return;
    const interval = window.setInterval(() => setConnectivityClock(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [canView, sessionId]);

  const runtimeSessionMismatch =
    runtime.data !== undefined &&
    sessionId !== null &&
    runtime.data.services.some((service) => service.sessionId !== sessionId);
  const runtimeFailed =
    runtime.isError ||
    runtime.isRefetchError ||
    runtimeSessionMismatch ||
    (!runtime.isPending && runtime.data === undefined);
  const trustedRuntime = runtimeFailed ? undefined : runtime.data;
  const activeConfirmedAssignment =
    confirmedAssignment?.actorId === operator.id &&
    confirmedAssignment.sessionId === sessionId &&
    confirmedAssignment.tableNumber === tableNumber
      ? confirmedAssignment
      : null;
  const confirmedAssignmentVisibleInRuntime =
    activeConfirmedAssignment !== null &&
    trustedRuntime?.services.some(
      (service) => service.id === activeConfirmedAssignment.serviceId,
    ) === true;
  const sessionServices = trustedRuntime?.services ?? [];
  const activeServices = sessionServices.filter(
    (service) => service.status === "ASSIGNED" || service.status === "ACTIVE",
  );
  const assignmentAvailabilityView = buildCoachSessionAssignmentAvailabilityView({
    profiles: trustedRuntime?.profiles ?? [],
    assignmentAvailability: trustedRuntime?.assignmentAvailability,
    queue: trustedRuntime?.queue ?? [],
    currentSessionCoachIds: new Set(activeServices.map((service) => service.coachId)),
    coachRotationEnabled: trustedRuntime?.coachRotationEnabled === true,
  });
  const selectedService = activeServices.find((service) => service.id === selectedServiceId);
  const coachDisplayNames = new Map(
    (trustedRuntime?.profiles ?? []).map((profile) => [profile.id, profile.displayName]),
  );
  const selectedCoachDisplayName = selectedService
    ? (coachDisplayNames.get(selectedService.coachId) ?? "助教姓名读取中")
    : null;
  const selectedServiceHasPendingStopRequest = Boolean(
    selectedService &&
      stopRequestDirectory.data?.items.some(
        (request) => request.status === "PENDING" && request.serviceId === selectedService.id,
      ),
  );
  const stopRequestDirectoryTrusted =
    !stopRequestDirectory.isPending && !stopRequestDirectory.isError && stopRequestDirectory.data;
  const availableActions = actionsFor(selectedService, operator.role).filter(
    (item) =>
      item !== "COMPLETE" ||
      (Boolean(stopRequestDirectoryTrusted) && !selectedServiceHasPendingStopRequest),
  );
  const connectivityState = connectivityTrustState(
    connectivity,
    Math.max(connectivityClock, Date.now()),
  );
  const storeAuthorityAvailable =
    connectivityState.kind === "TRUSTED" && connectivityState.data.offlineAuthorityAvailable;
  const rulesFailed = rules.isError || rules.isRefetchError;
  const rulesState = rulesFailed
    ? "ERROR"
    : rules.isPending
      ? "LOADING"
      : (rules.data?.length ?? 0) === 0
        ? "EMPTY"
        : "READY";

  const operationRecovery = readCoachOperationRecovery(identityKey);
  const assignmentRecovery = readCoachServiceAssignmentRecovery(assignmentContext);
  const replacementRecovery = readCoachServiceReplacementRecovery(replacementContext);
  const correctionRecovery = readCoachServiceCorrectionRecovery(correctionContext);
  const serviceIds = new Set(sessionServices.map((service) => service.id));
  const operationRecoveryMatches =
    operationRecovery.status !== "OWNED" ||
    (operationRecovery.recovery.command.kind === "TRANSITION"
      ? serviceIds.has(operationRecovery.recovery.command.serviceId)
      : operationRecovery.recovery.command.kind === "ASSIGN" &&
        operationRecovery.recovery.command.sessionId === sessionId);
  const assignmentRecoveryMatches =
    assignmentRecovery.status !== "OWNED" ||
    assignmentRecovery.recovery.command.request.sessionId === sessionId;
  const replacementRecoveryMatches =
    replacementRecovery.status !== "OWNED" ||
    replacementRecovery.recovery.command.baseline.sessionId === sessionId;
  const correctionRecoveryMatches =
    correctionRecovery.status !== "OWNED" ||
    serviceIds.has(correctionRecovery.recovery.command.preview.serviceId);
  const recoveryScopeMismatch =
    !operationRecoveryMatches ||
    !assignmentRecoveryMatches ||
    !replacementRecoveryMatches ||
    !correctionRecoveryMatches;
  const commonBlocked =
    !canOperate ||
    stopRequestRecoveryBlocked ||
    pending ||
    commandFlight.current ||
    runtimeFailed ||
    (activeConfirmedAssignment !== null && !confirmedAssignmentVisibleInRuntime) ||
    trustedRuntime === undefined ||
    rulesFailed ||
    connectivityState.kind !== "TRUSTED" ||
    !storeAuthorityAvailable ||
    recoveryScopeMismatch;

  useEffect(() => {
    if (!selectedService || !availableActions.includes(action)) {
      setAction(selectedService?.status === "ACTIVE" ? "COMPLETE" : "START");
      setReason("");
    }
  }, [action, availableActions, selectedService]);

  useEffect(() => {
    if (selectedServiceId && !activeServices.some((service) => service.id === selectedServiceId)) {
      setSelectedServiceId("");
      setReason("");
    }
  }, [activeServices, selectedServiceId]);

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["coach-runtime"] }),
      queryClient.invalidateQueries({ queryKey: ["coach-service-rules"] }),
      queryClient.invalidateQueries({ queryKey: ["tables"] }),
      queryClient.invalidateQueries({ queryKey: ["frontdesk-operating-facts"] }),
      queryClient.invalidateQueries({ queryKey: ["session-order", sessionId] }),
      queryClient.invalidateQueries({ queryKey: ["orders"] }),
    ]);
  }, [queryClient, sessionId]);

  const assignmentConfirmed = useCallback(
    (result: CoachServiceAssignmentOperatorResult, assignedSessionId: string): void => {
      if (
        !sessionId ||
        !tableNumber ||
        assignedSessionId !== sessionId ||
        result.tableNumber !== tableNumber
      ) {
        return;
      }
      const receipt = {
        actorId: operator.id,
        sessionId,
        serviceId: result.id,
        tableNumber,
      };
      queryClient.setQueryData(confirmedAssignmentReceiptKey, receipt);
      setConfirmedAssignment(receipt);
    },
    [confirmedAssignmentReceiptKey, operator.id, queryClient, sessionId, tableNumber],
  );

  const recoveryChanged = useCallback((): void => {
    setRecoveryRevision((current) => current + 1);
  }, []);

  const legacyAssignmentRecovery =
    operationRecovery.status === "OWNED" &&
    operationRecovery.recovery.state === "OUTCOME_UNKNOWN" &&
    operationRecovery.recovery.command.kind === "ASSIGN" &&
    operationRecovery.recovery.command.sessionId === sessionId
      ? operationRecovery.recovery
      : null;

  useEffect(() => {
    if (
      !legacyAssignmentRecovery ||
      connectivityState.kind !== "TRUSTED" ||
      !storeAuthorityAvailable ||
      pending ||
      commandFlight.current
    ) {
      return;
    }
    const command = legacyAssignmentRecovery.command;
    if (command.kind !== "ASSIGN") return;
    const probeKey = `${legacyAssignmentRecovery.ownerIdentityKey}:${command.idempotencyKey}`;
    if (legacyAssignmentProbe.current === probeKey) return;
    legacyAssignmentProbe.current = probeKey;
    commandFlight.current = true;
    setPending(true);
    setFeedback({
      kind: "INFO",
      text: "正在按旧分配的原请求号查询门店结果；查询清楚前不会生成或发送新请求。",
    });
    void recoverLegacyCoachServiceAssignment({
      sessionId: command.sessionId,
      coachId: command.coachId,
      assignmentType: command.assignmentType,
      serviceRuleId: command.serviceRuleId,
      idempotencyKey: command.idempotencyKey,
    })
      .then(async (resolution) => {
        if (!clearCoachOperationRecovery(legacyAssignmentRecovery, identityKey)) {
          setFeedback({
            kind: "ERROR",
            text: "旧分配结果已由门店确认，但本机无法清除原请求。当前台单继续锁定，请勿重试。",
          });
          return;
        }
        setFeedback({
          kind: "INFO",
          text:
            resolution.resolution === "LEGACY_ASSIGNED"
              ? "历史助教任务已经分配，请在当前任务中确认上钟。"
              : resolution.resolution === "NOT_FOUND_REQUIRES_NEW_REQUEST"
                ? "门店已明确确认旧分配没有执行。请重新选择当前球桌、助教和服务项目后发起。"
                : "原助教分配已经确认，当前台单已刷新。",
        });
        await refresh();
      })
      .catch((error) => {
        setFeedback({
          kind: "ERROR",
          text: `旧分配结果仍无法确认：${messageFrom(error)}。原请求继续保留，不会自动重发。`,
        });
      })
      .finally(() => {
        commandFlight.current = false;
        setPending(false);
        recoveryChanged();
      });
  }, [
    connectivityState.kind,
    identityKey,
    legacyAssignmentRecovery,
    pending,
    recoveryChanged,
    refresh,
    storeAuthorityAvailable,
  ]);

  const dispatch = useCallback(
    async (recovery: CoachOperationRecovery): Promise<void> => {
      const command = recovery.command;
      if (
        command.kind !== "TRANSITION" ||
        !sessionId ||
        !serviceIds.has(command.serviceId) ||
        commandFlight.current
      ) {
        setFeedback({
          kind: "ERROR",
          text: "原操作不属于当前台单，系统没有发送请求。请回到原球桌核对。",
        });
        return;
      }
      try {
        requireTrustedStoreAuthority(
          connectivity,
          command.label,
          Math.max(connectivityClock, Date.now()),
        );
      } catch (error) {
        setFeedback({ kind: "ERROR", text: messageFrom(error) });
        return;
      }
      const wasAlreadyUnknown = recovery.state === "OUTCOME_UNKNOWN";
      commandFlight.current = true;
      setPending(true);
      setFeedback({ kind: "INFO", text: `${command.label}正在处理，请勿重复点击。` });
      try {
        const result = await transitionCoachService(
          command.serviceId,
          command.action,
          command.reason,
          command.idempotencyKey,
        );
        if (result.sessionId !== sessionId) {
          throw new ApiRequestError(
            "GATEWAY_RESPONSE_INVALID",
            "门店返回了其他台单的助教服务，当前结果不会显示或继续操作",
          );
        }
        if (!clearCoachOperationRecovery(recovery, identityKey)) {
          setFeedback({
            kind: "ERROR",
            text: "门店已确认操作完成，但本机无法清除原请求。当前台单继续锁定，请勿重试。",
          });
          return;
        }
        setFeedback({ kind: "INFO", text: `${command.label}已完成，当前台单已刷新。` });
        setSelectedServiceId("");
        setReason("");
        await refresh();
      } catch (error) {
        const outcomeUnknown = coachOperationOutcomeIsUnknown(error);
        if (outcomeUnknown) {
          if (!markCoachOperationOutcomeUnknown(recovery, identityKey)) {
            setFeedback({
              kind: "ERROR",
              text: `${command.label}结果尚未确认，而且本机无法保存原请求。请勿重复操作。`,
            });
          } else {
            setFeedback({
              kind: "ERROR",
              text: `${command.label}结果尚未确认。原操作和原请求号已保留，只能使用同一次请求安全重试。`,
            });
          }
        } else if (wasAlreadyUnknown) {
          setFeedback({
            kind: "ERROR",
            text: `${command.label}本次重试没有执行，但原操作结果仍未确认。请恢复连接后继续使用原请求。`,
          });
        } else if (!clearCoachOperationRecovery(recovery, identityKey)) {
          setFeedback({
            kind: "ERROR",
            text: `${command.label}没有执行，但本机无法清除原请求。当前台单继续锁定。`,
          });
        } else {
          setFeedback({ kind: "ERROR", text: `${command.label}未完成：${messageFrom(error)}` });
        }
      } finally {
        commandFlight.current = false;
        setPending(false);
        recoveryChanged();
      }
    },
    [connectivity, connectivityClock, identityKey, recoveryChanged, refresh, serviceIds, sessionId],
  );

  const submitTransition = (): void => {
    if (!selectedService || !availableActions.includes(action)) return;
    const needsReason = action === "COMPLETE" && operator.role === "MANAGER";
    const normalizedReason = reason.trim();
    if (needsReason && ([...normalizedReason].length < 2 || [...normalizedReason].length > 500)) {
      setFeedback({
        kind: "ERROR",
        text: "店长代办下钟必须填写 2 至 500 个字，说明代办原因和现场核对结果。",
      });
      return;
    }
    const command: CoachRuntimeOperationCommand = {
      kind: "TRANSITION",
      serviceId: selectedService.id,
      action,
      reason: needsReason ? normalizedReason : null,
      idempotencyKey: nextTransitionKey(),
      label: operationLabel(action),
    };
    const claimed = claimCoachOperationRecovery(identityKey, command);
    recoveryChanged();
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        text: "本机已有一笔助教操作等待核对，系统没有发送新请求。请先处理原操作。",
      });
      return;
    }
    void dispatch(claimed.recovery);
  };

  if (!canView) return null;

  if (!table || !sessionId) {
    return (
      <section className="coach-panel" aria-label="当前台单助教服务">
        <p className="disabled-reason">
          请先选择一张状态为“使用中”的球桌。没有当前台单时不会读取或执行助教操作。
        </p>
      </section>
    );
  }

  const readUnavailable = runtimeFailed || rulesFailed || connectivityState.kind !== "TRUSTED";
  const authorityUnavailable =
    connectivityState.kind === "TRUSTED" && !connectivityState.data.offlineAuthorityAvailable;
  const foreignRecovery = recoveryScopeMismatch ? (
    <div className="error-band" role="alert">
      <TriangleAlert size={17} aria-hidden="true" />
      <span>
        本机保存着另一台单的助教原请求。当前只显示并处理 {table.tableNumber}{" "}
        号桌，请回到原球桌核对。
      </span>
    </div>
  ) : null;
  const assignmentBlocked =
    commonBlocked ||
    !assignmentAvailabilityView.trusted ||
    operationRecovery.status !== "NONE" ||
    replacementRecovery.status !== "NONE" ||
    correctionRecovery.status !== "NONE";
  const replacementBlocked =
    commonBlocked ||
    operationRecovery.status !== "NONE" ||
    assignmentRecovery.status !== "NONE" ||
    correctionRecovery.status !== "NONE";
  const correctionBlocked =
    commonBlocked ||
    operationRecovery.status !== "NONE" ||
    assignmentRecovery.status !== "NONE" ||
    replacementRecovery.status !== "NONE";
  const transitionBlocked =
    commonBlocked ||
    operationRecovery.status !== "NONE" ||
    assignmentRecovery.status !== "NONE" ||
    replacementRecovery.status !== "NONE" ||
    correctionRecovery.status !== "NONE";

  return (
    <section className="coach-session-operations" aria-label={`${table.tableNumber} 号桌助教服务`}>
      <div className="coach-section-title">
        <div>
          <span>当前台单助教服务</span>
          <h2>只处理当前 {table.tableNumber} 号桌</h2>
        </div>
        <Clock3 size={21} aria-hidden="true" />
      </div>
      <p className="coach-form-note">
        当前台单号已固定。这里不会显示或操作其他球桌的助教服务；切换球桌后会重新读取对应台单。
      </p>

      {foreignRecovery}
      {activeConfirmedAssignment && (readUnavailable || !confirmedAssignmentVisibleInRuntime) ? (
        <output className="coach-form-note">
          本次助教分配已确认成功。当前台单状态正在重新读取，请勿重复分配；读取恢复后再办理其他操作。
        </output>
      ) : null}
      {authorityUnavailable ? (
        <div className="error-band" role="alert">
          <TriangleAlert size={17} aria-hidden="true" />
          <span>
            门店离线经营权限已失效。当前台单和助教状态仍可查看，但派钟、上下钟、换人和纠错已暂停。
          </span>
        </div>
      ) : null}
      {runtime.isPending || rules.isPending || connectivity.isPending ? (
        <p className="coach-empty">正在读取当前台单的助教服务…</p>
      ) : readUnavailable ? (
        <div className="error-band" role="alert">
          <TriangleAlert size={17} aria-hidden="true" />
          <span>
            当前台单的助教状态、服务项目或门店连接无法安全确认，旧内容已隐藏，操作已暂停。
          </span>
        </div>
      ) : (
        <>
          <CoachServiceStopRequestPanel
            operator={operator}
            effectiveRoles={effectiveRoles}
            activeServices={activeServices}
            scopeServiceIds={sessionServices.map((service) => service.id)}
            onRecoveryBlockedChange={setStopRequestRecoveryBlocked}
            onCompleted={refresh}
          />

          {canOperate ? (
            <CoachServiceAssignmentPanel
              operator={operator}
              managerReasonRequired={isManager}
              activeTables={[table]}
              tableReadFailed={false}
              assignableCoaches={assignmentAvailabilityView.requestedCoaches}
              rotationEnabled={trustedRuntime?.coachRotationEnabled === true}
              rotationCoachId={assignmentAvailabilityView.rotationCoachId}
              rotationAssignmentEmptyReason={assignmentAvailabilityView.rotationEmptyReason}
              requestedAssignmentEmptyReason={assignmentAvailabilityView.requestedEmptyReason}
              serviceOptions={rulesState === "READY" ? (rules.data ?? []) : []}
              serviceOptionsState={rulesState}
              blocked={assignmentBlocked}
              onPreview={previewCoachServiceAssignment}
              onRecoveryViewChange={recoveryChanged}
              onCompleted={refresh}
              onConfirmed={assignmentConfirmed}
            />
          ) : (
            <p className="coach-form-note">
              老板账号只查看当前服务和下钟申请；日常派钟、上下钟与换人由收银员或店长办理。
            </p>
          )}

          <section className="coach-panel" aria-label="当前台单助教上下钟">
            <div className="coach-section-title compact">
              <div>
                <span>当前台单服务</span>
                <h3>上钟、下钟与受控结束</h3>
              </div>
              <BadgeCheck size={20} aria-hidden="true" />
            </div>
            <div className="coach-service-summary" aria-live="polite">
              <strong>当前 {activeServices.length} 位</strong>
              <span>每位助教独立计费、独立操作；单独下钟不会结束同桌其他助教的服务。</span>
            </div>
            {activeServices.length === 0 ? (
              <p className="coach-empty">
                当前 {table.tableNumber} 号桌没有待上钟或服务中的助教服务。
              </p>
            ) : (
              <div className="coach-service-list">
                {activeServices.map((service) => {
                  const displayName = coachDisplayNames.get(service.coachId) ?? "助教姓名读取中";
                  const serviceActions = actionsFor(service, operator.role);
                  return (
                    <button
                      type="button"
                      className={selectedServiceId === service.id ? "is-selected" : ""}
                      key={service.id}
                      aria-label={`选择${displayName}，单独办理${service.status === "ACTIVE" ? "下钟" : "上钟"}`}
                      disabled={
                        transitionBlocked ||
                        serviceActions.length === 0 ||
                        (service.status === "ACTIVE" &&
                          (!stopRequestDirectoryTrusted ||
                            stopRequestDirectory.data.items.some(
                              (request) =>
                                request.status === "PENDING" && request.serviceId === service.id,
                            )))
                      }
                      onClick={() => {
                        setSelectedServiceId(service.id);
                        setAction(service.status === "ACTIVE" ? "COMPLETE" : "START");
                        setReason("");
                      }}
                    >
                      <span className="coach-service-card-heading">
                        <strong>{displayName}</strong>
                        <span className={`coach-service-state coach-service-${service.status}`}>
                          {serviceStatusLabels[service.status]}
                        </span>
                      </span>
                      <dl className="coach-service-card-facts">
                        <div>
                          <dt>服务项目</dt>
                          <dd>{service.serviceTypeName}</dd>
                        </div>
                        <div>
                          <dt>开始时间</dt>
                          <dd>{serviceStartTime(service)}</dd>
                        </div>
                        <div>
                          <dt>独立计费规则</dt>
                          <dd>{servicePricingRule(service)}</dd>
                        </div>
                      </dl>
                      <small className="coach-service-card-action">
                        {serviceActions.length > 0
                          ? `选择后只办理这位助教的${service.status === "ACTIVE" ? "下钟" : "上钟"}`
                          : "当前账号只可查看这位助教的服务"}
                      </small>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedService && canOperate && availableActions.length > 0 ? (
              <form
                name="coach-session-service-transition"
                autoComplete="off"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitTransition();
                }}
              >
                <p className="coach-service-selection-note">
                  <strong>当前选择：{selectedCoachDisplayName}</strong>
                  <span>
                    本次只操作这位助教；同桌其他助教继续服务，之后仍可继续派其他可用助教。
                  </span>
                </p>
                <label>
                  本次操作
                  <select
                    value={action}
                    disabled={commonBlocked || operationRecovery.status !== "NONE"}
                    onChange={(event) => {
                      setAction(event.target.value as TransitionAction);
                      setReason("");
                    }}
                  >
                    {availableActions.map((item) => (
                      <option value={item} key={item}>
                        {transitionLabels[item]}
                      </option>
                    ))}
                  </select>
                </label>
                {action === "COMPLETE" && operator.role === "MANAGER" ? (
                  <label>
                    店长代办原因和核对结果
                    <textarea
                      value={reason}
                      maxLength={500}
                      disabled={commonBlocked || operationRecovery.status !== "NONE"}
                      placeholder="例如：晚班收银员临时缺席，已现场核对服务时间"
                      onChange={(event) => setReason(event.target.value)}
                    />
                    <small>
                      必须说明为什么由店长代办，以及已经核对了什么；内容会保存在服务记录中。
                    </small>
                  </label>
                ) : null}
                <button
                  className="button secondary"
                  type="submit"
                  disabled={
                    transitionBlocked ||
                    (action === "COMPLETE" &&
                      operator.role === "MANAGER" &&
                      reason.trim().length < 2)
                  }
                >
                  <BadgeCheck size={17} aria-hidden="true" /> {transitionLabels[action]}
                </button>
              </form>
            ) : null}

            {operationRecovery.status === "OWNED" && operationRecoveryMatches ? (
              <div className="coach-form-note" role="alert">
                <strong>原助教操作结果待确认</strong>
                <span>
                  {operationRecovery.recovery.command.kind === "ASSIGN"
                    ? "旧分配的原操作人、台单和请求号已固定，系统只查询原请求，不会生成新请求。"
                    : "原操作人、服务、动作和请求号已固定，不能改成另一笔。"}
                </span>
                {operationRecovery.recovery.state === "OUTCOME_UNKNOWN" &&
                operationRecovery.recovery.command.kind === "TRANSITION" ? (
                  <button
                    className="button secondary"
                    type="button"
                    disabled={pending || !storeAuthorityAvailable || runtimeFailed}
                    onClick={() => void dispatch(operationRecovery.recovery)}
                  >
                    使用原请求安全重试
                  </button>
                ) : null}
              </div>
            ) : operationRecovery.status !== "NONE" ? (
              <div className="error-band" role="alert">
                当前账号不能安全处理本机保存的助教原操作，当前台单已暂停。
              </div>
            ) : null}

            {feedback ? (
              <output className={feedback.kind === "ERROR" ? "error-band" : "coach-form-note"}>
                {feedback.text}
              </output>
            ) : null}

            {replacementRecoveryMatches && isManager ? (
              <CoachServiceReplacementPanel
                operator={operator}
                selectedService={selectedService}
                replacementCoaches={(trustedRuntime?.profiles ?? []).filter((profile) => {
                  const shift = trustedRuntime?.shifts.find((item) => item.coachId === profile.id);
                  return (
                    profile.status === "ENABLED" &&
                    (shift?.status === "AVAILABLE" || shift?.status === "QUEUED") &&
                    !activeServices.some((service) => service.coachId === profile.id)
                  );
                })}
                showCommissionAmount={effectiveRoles.some(
                  (role) => role === "OWNER" || role === "MANAGER",
                )}
                blocked={replacementBlocked}
                onRecoveryViewChange={recoveryChanged}
                onCompleted={refresh}
              />
            ) : null}
          </section>

          {correctionRecoveryMatches && isManager ? (
            <CoachServiceCorrectionPanel
              operator={operator}
              effectiveRoles={effectiveRoles}
              selectedService={selectedService}
              selectedCoachDisplayName={
                selectedService
                  ? trustedRuntime?.profiles.find(
                      (profile) => profile.id === selectedService.coachId,
                    )?.displayName
                  : undefined
              }
              blocked={correctionBlocked}
              onRecoveryViewChange={recoveryChanged}
              onCompleted={refresh}
            />
          ) : null}
        </>
      )}
    </section>
  );
}
